import React, { useState, useEffect } from 'react';
import { Clock, Flame, ShoppingBag, Sparkles, Lock } from 'lucide-react';
import { apiClient } from '../../api/client';

const RARITY_COLORS = {
  rare: { bg: 'from-blue-500 to-cyan-500', badge: 'bg-blue-100 text-blue-700' },
  epic: { bg: 'from-violet-500 to-purple-600', badge: 'bg-violet-100 text-violet-700' },
  legendary: { bg: 'from-amber-500 to-orange-500', badge: 'bg-amber-100 text-amber-700' },
};

const LimitedDropCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    apiClient.request('/api/enhancements/limited-drop')
      .then(d => { setData(d); setClaimed(d.user_claimed); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!data?.seconds_until) return;
    const endTime = Date.now() + data.seconds_until * 1000;
    const tick = () => {
      const rem = Math.max(0, endTime - Date.now());
      const h = Math.floor(rem / 3600000);
      const m = Math.floor((rem % 3600000) / 60000);
      const s = Math.floor((rem % 60000) / 1000);
      if (h > 24) setCountdown(`${Math.floor(h / 24)}d ${h % 24}h`);
      else if (h > 0) setCountdown(`${h}h ${m}m`);
      else setCountdown(`${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [data]);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await apiClient.request('/api/enhancements/limited-drop/claim', { method: 'POST' });
      setClaimed(true);
    } catch (e) {
      alert(e.message || 'Failed to claim drop');
    } finally { setClaiming(false); }
  };

  if (loading) {
    return (
      <div data-testid="limited-drop-card" className="rounded-2xl overflow-hidden animate-pulse">
        <div className="h-44 bg-gray-200" />
      </div>
    );
  }
  if (!data) return null;

  const drop = data.drop;
  const colors = RARITY_COLORS[drop.rarity] || RARITY_COLORS.rare;
  const pctClaimed = drop.max_claims > 0 ? Math.round((data.claimed_count / drop.max_claims) * 100) : 0;

  return (
    <div data-testid="limited-drop-card" className="rounded-2xl overflow-hidden">
      <div className={`bg-gradient-to-br ${colors.bg} p-5 relative`} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>
        {/* Decorative */}
        <div className="absolute top-2 right-2 opacity-10">
          <Flame className="w-24 h-24 text-white" />
        </div>

        {/* Status badge + timer */}
        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className="flex items-center gap-2">
            {data.is_live ? (
              <span className="px-2.5 py-1 rounded-full bg-white/35 backdrop-blur-sm text-white text-sm font-bold flex items-center gap-1.5 animate-pulse">
                <Flame className="w-3.5 h-3.5" /> LIVE DROP
              </span>
            ) : data.status === 'upcoming' ? (
              <span className="px-2.5 py-1 rounded-full bg-white/30 text-white text-sm font-bold flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Drops in {countdown}
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full bg-white/30 text-white text-sm font-bold">
                Ended — Next drop in {countdown}
              </span>
            )}
            <span className={`px-2.5 py-0.5 rounded-full text-sm font-extrabold ${colors.badge}`}>
              {drop.rarity}
            </span>
          </div>
          {data.is_live && (
            <span className="text-white text-sm font-semibold flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {countdown}
            </span>
          )}
        </div>

        {/* Drop info */}
        <div className="relative z-10 mb-3">
          <p className="text-sm text-white font-extrabold uppercase tracking-wider mb-1">Limited Edition Drop</p>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">{drop.title}</h3>
          <p className="text-base text-white font-semibold mt-1.5 leading-snug">{drop.description}</p>
        </div>

        {/* Stock bar */}
        <div className="relative z-10 mb-4">
          <div className="flex items-center justify-between text-sm text-white font-bold mb-1">
            <span>{data.remaining}/{drop.max_claims} remaining</span>
            <span>{pctClaimed}% claimed</span>
          </div>
          <div className="w-full h-2.5 bg-white/30 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pctClaimed}%` }} />
          </div>
        </div>

        {/* CTA */}
        <div className="relative z-10">
          {data.is_live && !claimed && data.remaining > 0 && (
            <button
              data-testid="claim-drop-btn"
              onClick={handleClaim}
              disabled={claiming}
              className="w-full py-2.5 rounded-xl bg-white text-gray-900 font-bold text-base hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ textShadow: 'none' }}
            >
              <ShoppingBag className="w-4 h-4" />
              {claiming ? 'Claiming...' : `Claim for ${drop.cost_hearts} Hearts`}
            </button>
          )}
          {claimed && (
            <div className="w-full py-2.5 rounded-xl bg-white/30 text-white font-bold text-base text-center flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-yellow-200" /> Claimed — Check your vault!
            </div>
          )}
          {data.is_live && !claimed && data.remaining === 0 && (
            <div className="w-full py-2.5 rounded-xl bg-white/25 text-white font-bold text-base text-center">
              Sold Out
            </div>
          )}
          {!data.is_live && !claimed && (
            <div className="w-full py-2.5 rounded-xl bg-white/25 text-white font-bold text-base text-center flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" /> {data.status === 'upcoming' ? 'Coming Friday 6PM UTC' : 'Next drop Friday'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LimitedDropCard;
